import { TeamInfoWithAdmin } from '@common/queries/team';
import { Entity } from '@common/types/Entity';
import useOptimisticState from '@perceived/use-optimistic-state';
import { LoadingDIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Stack } from '@prophecy/ui-v3/Layout';
import { Switch } from '@prophecy/ui-v3/Switch';
import { Text } from '@prophecy/ui-v3/Typography';
import { parseJSON } from '@prophecy/utils/string';
import { useQueryClient } from '@tanstack/react-query';
import { useGlobalVariables } from 'frontend/core/src/context/globalConfig/securedConfigHook';
import { useGraphQlMutation } from 'frontend/core/src/data/util';
import styled from 'styled-components';

import { UpdateCodeGenerationQuery } from '../queries';

const CodeGenerationContainer = styled(Stack)`
  padding: 0 ${theme.spaces.x24};
`;

export default function CodeGenerationTab({ team }: { team: TeamInfoWithAdmin }) {
  const codeGenerationSettings = parseJSON<{
    codegenSettings?: {
      multiFileCodegen?: false;
    };
  }>(team?.adminInfo?.[0]?.AspectValue || '{}', { codegenSettings: { multiFileCodegen: false } }).codegenSettings;
  const client = useQueryClient();
  const aspectMutation = useGraphQlMutation(UpdateCodeGenerationQuery, {
    onSuccess: () => {
      client.invalidateQueries([Entity.Team]);
    }
  });
  const globalConfig = useGlobalVariables();
  const isMultiFileCodegenEnabled =
    codeGenerationSettings?.multiFileCodegen ?? globalConfig.isIncrementalCodegenEnabled;

  const { state: multiFileCodegen, updateState: setMultiFileCodegen } = useOptimisticState(
    isMultiFileCodegenEnabled,
    (checked: boolean) => {
      return aspectMutation.mutateAsync({
        teamUID: team._id,
        enabled: checked
      });
    }
  );

  return (
    <CodeGenerationContainer gap={theme.spaces.x12} height='100%'>
      <Text level='lg' weight={theme.fontWeight.medium} tone={theme.colors.gray900}>
        Code Generation
      </Text>
      <Stack gap={theme.spaces.x12} direction='horizontal'>
        <Switch onChange={setMultiFileCodegen} checked={multiFileCodegen} data-test-id='codegen-multi-file-switch'>
          Enable multi file code generation
        </Switch>
        {aspectMutation.isLoading && <LoadingDIcon type='extended' spin />}
      </Stack>
    </CodeGenerationContainer>
  );
}
